/**
 * Doc-slide STREAMING — the live element-by-element surface for open-slide-class doc slides.
 *
 * Why this exists: a doc slide renders as `<iframe srcDoc={docHtml}>`, and swapping srcDoc reloads the
 * whole document — the old "stream" (patch docHtml every 220ms) was really 4 full reloads a second, every
 * entrance animation restarting each time. That strobe is what Guri called "flickering".
 *
 * The replacement: while a generation streams, the card mounts ONE persistent sandboxed iframe (the
 * STREAM SHELL below) and feeds it text over postMessage. The shell re-parses the accumulated source with
 * DOMParser and syncs the parsed tree into its live stage IN PLACE — nodes that already exist are never
 * recreated, so their CSS animations run exactly once and new elements visibly "write in" as they arrive.
 * Each element gets the same shimmer flash the flow pipeline paints per streamed element (BirtvyEditShimmer
 * visual, duplicated inside the shell — the parent cannot measure into an opaque-origin iframe, so the
 * flashes must live where the elements are measurable).
 *
 * THE DRAIN (measured, not modeled): arrival IS the generation pace — measured cadence here is ~68B every
 * ~68ms, already smooth — so the drain applies bytes the moment they arrive and does exactly one thing on
 * top: a genuine BURST (a multi-KB chunk that would land several finished elements in one frame) is spread
 * over ~3 ticks (~120ms). No estimated rate: the first version of this file modeled the pace with an EWMA
 * whose first sample swallowed the model's thinking latency — the drain crawled behind a growing backlog
 * all run and the end-of-stream flush slammed the rest at once ("pauses, then flickers", observed).
 * Zero added latency in the common case, bounded ~160ms behind a burst, never ahead of arrived bytes.
 * Every run still logs its stats (chunks, gap percentiles) as `[docStream] …` so pacing stays a numbers
 * conversation.
 *
 * EDIT SEEDING: an edit of an existing slide starts the shell from the CURRENT document (hello.seed) and
 * the incoming stream writes OVER it — identical prefix untouched, changed nodes replaced positionally
 * as they arrive, the old tail kept until the stream passes it (Guri: keep it and write over it, never
 * blank it first). Trailing leftovers are trimmed only at done.
 *
 * Kept OUT of React state on purpose — per-chunk setState was the old strobe's other half.
 */

type DocStreamEvent = { delta?: string; refresh?: string; done?: boolean }
type DocStreamListener = (ev: DocStreamEvent) => void

const TICK_MS = 40
/** Backlog at or below this applies in ONE tick — the zero-added-latency common case. */
const INSTANT_FLOOR = 400
/** A genuine burst spreads over at most this many ticks (~120ms) instead of landing in one frame. */
const SPREAD_TICKS = 3
/** After end() the floor rises so the tail finishes in a couple of ticks — brisk, never a slam. */
const END_FLOOR = 1200

interface Entry {
  target: string
  sent: number
  lastPushT: number
  startedT: number
  ended: boolean
  drained: boolean
  timer: number | null
  stats: { chunks: number; bytes: number; gaps: number[]; sizes: number[] }
}

const entries = new Map<number, Entry>()
const listeners = new Map<number, Set<DocStreamListener>>()

function emit(id: number, ev: DocStreamEvent) {
  const subs = listeners.get(id)
  if (!subs) return
  for (const cb of subs) cb(ev)
}

function stopTimer(e: Entry) {
  if (e.timer != null) {
    window.clearInterval(e.timer)
    e.timer = null
  }
}

function finish(id: number, e: Entry) {
  if (e.drained) return // tick and end() can both reach here on the same fully-sent entry
  e.drained = true
  stopTimer(e)
  emit(id, { done: true })
  const gaps = e.stats.gaps.slice().sort((a, b) => a - b)
  const p = (q: number) => (gaps.length ? Math.round(gaps[Math.min(gaps.length - 1, Math.floor(gaps.length * q))]) : 0)
  const big = e.stats.sizes.filter((s) => s > INSTANT_FLOOR).length
  // The measurement half: real chunk cadence for this run, so pacing stays a numbers conversation.
  console.debug(
    `[docStream] slide ${id}: ${e.stats.chunks} chunks, ${e.stats.bytes}B in ${Math.round(performance.now() - e.startedT)}ms, ` +
      `gap p50/p90 ${p(0.5)}/${p(0.9)}ms, mean chunk ${e.stats.chunks ? Math.round(e.stats.bytes / e.stats.chunks) : 0}B, ` +
      `${big} burst(s) spread`,
  )
  window.setTimeout(() => {
    if (entries.get(id) === e) entries.delete(id)
  }, 5000)
}

function tick(id: number, e: Entry) {
  const behind = e.target.length - e.sent
  if (behind <= 0) {
    if (e.ended) finish(id, e)
    else stopTimer(e) // idle between arrivals — push() restarts the timer
    return
  }
  // Small backlog → apply it all this tick (tracks arrival exactly). Big backlog → a genuine burst,
  // spread over at most SPREAD_TICKS ticks so several finished elements don't land in one frame.
  const floor = e.ended ? END_FLOOR : INSTANT_FLOOR
  const n = behind <= floor ? behind : Math.max(floor, Math.ceil(behind / SPREAD_TICKS))
  let end = e.sent + n
  // Never end a delta inside a tag: a boundary like "…Throughput</" renders the dangling "</" as literal
  // text for a tick (seen live). Hold back to the last '<' unless the tag has closed — the held bytes
  // flow on the next tick once '>' arrives. A fully-drained ended stream is exempt (nothing to hold for).
  if (end < e.target.length) {
    const slice = e.target.slice(e.sent, end)
    const open = slice.lastIndexOf('<')
    // …but never let the hold-back STALL the drain: if holding would emit nothing while a real backlog
    // waits (a tag longer than this tick's whole budget), ship the raw slice — one briefly-visible
    // partial tag beats a stuck stream.
    if (open >= 0 && slice.indexOf('>', open) === -1 && (open > 0 || behind < 512)) end = e.sent + open
  }
  if (end > e.sent) {
    emit(id, { delta: e.target.slice(e.sent, end) })
    e.sent = end
  }
  if (e.ended && e.sent >= e.target.length) finish(id, e)
}

function ensureTimer(id: number, e: Entry) {
  if (e.timer != null) return
  e.timer = window.setInterval(() => tick(id, e), TICK_MS)
}

export const docStream = {
  /** Open a stream for a slide. Call before the first push; resets any stale state. */
  start(id: number) {
    const old = entries.get(id)
    if (old) stopTimer(old)
    entries.set(id, {
      target: '',
      sent: 0,
      lastPushT: performance.now(),
      startedT: performance.now(),
      ended: false,
      drained: false,
      timer: null,
      stats: { chunks: 0, bytes: 0, gaps: [], sizes: [] },
    })
  },
  /**
   * Push the FULL accumulated (cleaned) document so far; the drain forwards it (instantly in the common
   * case, spread when it's a burst). If the cleaned text stops being a prefix-extension (a mid-stream
   * fence strip re-cut the tail), subscribers get a `refresh` with the already-drained portion re-based
   * so the shell rebuilds once instead of corrupting.
   */
  push(id: number, full: string) {
    const e = entries.get(id)
    if (!e || e.ended || full === e.target) return
    const now = performance.now()
    const grew = full.length > e.target.length && full.startsWith(e.target)
    if (grew) {
      const dt = now - e.lastPushT
      if (dt > 0) e.stats.gaps.push(dt)
      e.stats.chunks += 1
      e.stats.bytes += full.length - e.target.length
      e.stats.sizes.push(full.length - e.target.length)
    } else {
      // Non-monotonic rewrite: re-base what subscribers already have, keep draining the rest.
      emit(id, { refresh: full.slice(0, Math.min(e.sent, full.length)) })
      e.sent = Math.min(e.sent, full.length)
    }
    e.lastPushT = now
    e.target = full
    ensureTimer(id, e)
  },
  /** Mark the source complete. The paced drain finishes the tail, THEN emits done to subscribers. */
  end(id: number) {
    const e = entries.get(id)
    if (!e || e.ended) return
    e.ended = true
    if (e.sent >= e.target.length) finish(id, e)
    else ensureTimer(id, e)
  },
  /**
   * Subscribe a shell to a slide's stream. Replays the DRAINED portion immediately (a card that remounts
   * mid-stream — pan away and back — catches up to the pacer, not past it), then receives increments.
   * Returns unsubscribe.
   */
  subscribe(id: number, cb: DocStreamListener): () => void {
    let subs = listeners.get(id)
    if (!subs) {
      subs = new Set()
      listeners.set(id, subs)
    }
    subs.add(cb)
    const e = entries.get(id)
    if (e) {
      if (e.sent > 0) cb({ delta: e.target.slice(0, e.sent) })
      if (e.drained) cb({ done: true })
    }
    return () => {
      const s = listeners.get(id)
      if (!s) return
      s.delete(cb)
      if (s.size === 0) listeners.delete(id)
    }
  },
}

/**
 * The stream shell document. Self-contained, rendered in `sandbox="allow-scripts"` (opaque origin — the
 * streamed model output still can never reach the app, exactly like the resting doc iframe). Message
 * protocol (parent → shell, `{__bds:1}` marker):
 *   hello {skeleton, seed, ground} → skeleton bars while a BLANK slide waits; seed = the current
 *     document for an EDIT, rendered into the stage immediately so the stream writes OVER it;
 *     ground = an opaque white base under the stream. The show itself is REAL elements: the stage syncs
 *     the arriving document exactly as streamed — its own styles, its own layout — element by element,
 *     each flashed by the arrival shimmer below. (A skeleton-block skin briefly replaced this on
 *     2026-08-16 and was reversed the same day once Guri watched it live: "can you stream real
 *     elements? …reuse that same kind of highlight." The waiting bars remain the skeleton's only job.)
 *     The ground exists for EDITS, which mount the shell over the HELD current document: without it the
 *     old slide shows through the transparent body until the new document's own background arrives, and
 *     the two designs mix — the half-dark/half-light hybrid that got the seeded write-over vetoed. It is
 *     a plain (non-!important) rule injected BEFORE any synced style, so the streamed document's own
 *     body background overrides it the moment it arrives.
 *   chunk {s} → append paced delta and re-sync;  refresh {s} → replace source, rebuild once;
 *   done → trim seeded leftovers, flush remaining flashes, run the document's scripts (DOMParser scripts
 *     are inert by spec, so count-ups run exactly once, at completion).
 *
 * Sync: full-tree pairwise walk (index + nodeName + shallow attrs). Identical nodes are untouched
 * (comparisons only — no recreation, no animation restart); growing text gets nodeValue updates; a
 * mismatch REPLACES that node positionally (the write-over morph) and later seeded siblings stay visible
 * until the stream reaches them; missing nodes are appended. Stream-arrived elements flash (the shimmer)
 * the moment the write head moves past them; seeded content never flashes.
 */
export function docStreamShellHtml(): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
html,body{margin:0;width:1920px;height:1080px;overflow:hidden;background:transparent}
#stage{position:absolute;inset:0;overflow:hidden}
#lay{position:absolute;inset:0;pointer-events:none;z-index:2147483647}
#skel{position:absolute;inset:0;padding:120px;display:none}
/* Skeleton bars: a quiet opacity pulse, not a gradient sweep. The old background-position shimmer
   repainted every bar every frame for the whole head-streaming wait (30-40s on a slow model), and read
   as constant noise — Guri, 2026-08-18: "the actual loader … is definitely not lightweight". Opacity
   animates on the compositor: zero paint, and the calm breathing is the lightweight read he asked for. */
.sb{border-radius:18px;background:rgba(0,0,0,0.07);animation:sbp 1.6s ease-in-out infinite alternate}
@keyframes sbp{from{opacity:.5}to{opacity:1}}
.flash{position:absolute;pointer-events:none;overflow:hidden;background:rgba(59,130,246,0.12);box-shadow:inset 0 0 0 2px rgba(59,130,246,0.5);border-radius:10px;animation:ff 1.15s ease-out forwards}
.flash::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,transparent 25%,rgba(255,255,255,0.7) 50%,transparent 75%);transform:translateX(-100%);animation:fsw .85s ease-in-out}
@keyframes fsw{to{transform:translateX(100%)}}
@keyframes ff{0%{opacity:1}65%{opacity:1}100%{opacity:0}}
/* Unresolved logo declarations: the contract has the model emit <img data-logo="…"> with NO src (intent
   only — artifactLogos fills src and strips the marker 1-3s after commit), and during streaming that
   declaration rendered as the broken-image glyph + alt text. Same self-clearing selector docBridge now
   injects into the live document: "declared, not yet resolved" hides; the fill reveals by removing the
   marker. Never a generic img[src=""] rule — that could hide a legitimately-loading image. */
img[data-logo]{visibility:hidden}
@media (prefers-reduced-motion: reduce){.flash{animation-duration:1ms}.flash::after{animation:none}.sb{animation:none}}
</style></head><body>
<div id="stage"></div>
<div id="lay"></div>
<div id="skel"><div class="sb" style="width:46%;height:120px;margin-bottom:56px"></div><div class="sb" style="width:72%;height:44px;margin-bottom:28px"></div><div class="sb" style="width:64%;height:44px"></div></div>
<script>
(function(){
'use strict';
var stage=document.getElementById('stage'),lay=document.getElementById('lay'),skel=document.getElementById('skel');
var parser=new DOMParser();
var raw='';
var done=false;
var seeded=false;
var headStyles=[];
var seedStyles=[];
var quietStyle=null;
var linksAdded={};
var scriptsRan=false;
var inserted=[];
var flashed=new WeakSet();
var seededSet=new WeakSet();
var active=[];

window.addEventListener('message',function(e){
  var m=e.data;
  if(!m||m.__bds!==1)return;
  /* incident probe 2026-08-18 (log-only): the shell was phase-live on /canvas yet painted nothing. */
  try{console.debug('[doc-reveal] shell msg '+m.t+(m.s?' +'+m.s.length+'B':''))}catch(e){}
  if(m.t==='hello'){
    if(m.skeleton)skel.style.display='block';
    if(m.ground)installGround();
    if(m.seed){seeded=true;plantSeed(String(m.seed))}
    return;
  }
  if(m.t==='chunk'){raw+=m.s;render();return}
  if(m.t==='refresh'){raw=m.s;hardReset();render();return}
  if(m.t==='done'){done=true;render();finishFlashes();runScripts()}
});

/* THE GROUND: an opaque white base under the streaming document. Exists for EDITS, where the shell sits
   over the HELD current slide — a transparent body would show the old design through until the new
   document's own body background streams in, mixing the two (the vetoed hybrid). Deliberately a PLAIN
   rule, no !important, appended BEFORE any synced style: the arriving document's own background wins the
   cascade the moment it exists, so a dark artifact streams dark. On a blank slide this matches the
   card's own white and changes nothing. */
function installGround(){
  var s=document.createElement('style');
  s.textContent='html,body{background:#fff}';
  document.head.appendChild(s);
}

/* An EDIT starts from the current document: render the seed into the stage (and its styles into the
   head) so the stream visibly writes OVER the existing slide. Seeded nodes are marked so they never
   flash and their entrance animations are neutralised — the slide must look AT REST, not re-entering. */
function plantSeed(html){
  var doc;
  try{doc=parser.parseFromString(html,'text/html')}catch(err){return}
  if(!doc||!doc.body)return;
  /* The seed's stylesheet lives in its OWN elements (seedStyles), NOT headStyles: the streamed document's
     styles must never overwrite it mid-parse — that de-styled the whole kept slide for the seconds the new
     style block took to arrive (measured live: several seconds of raw text soup). Instead the streamed
     styles are appended AFTER these, override where they conflict (same class names, later wins), and the
     seed styles are dropped only once the streamed head is COMPLETE (the body has started). */
  var i,els=doc.head?doc.head.children:[];
  for(i=0;i<els.length;i++){
    var el=els[i];
    if(el.tagName==='STYLE'){
      var st=document.createElement('style');
      st.textContent=el.textContent;
      document.head.appendChild(st);
      seedStyles.push(st);
    }else if(el.tagName==='LINK'){
      var href=el.getAttribute('href')||'';
      if(href&&!linksAdded[href]){
        linksAdded[href]=1;
        var l=document.createElement('link');
        l.rel=el.getAttribute('rel')||'stylesheet';l.href=href;
        document.head.appendChild(l);
      }
    }
  }
  var kids=sig(doc.body);
  for(i=0;i<kids.length;i++){
    var node=kids[i].cloneNode(true);
    stage.appendChild(node);
    if(node.nodeType===1)markSeeded(node);
  }
  /* Seeded nodes must look AT REST, not re-run their entrances. A huge NEGATIVE delay jumps every
     animation deep into its own timeline: finite entrances land on their fill-both END state, infinite
     ambience keeps cycling normally. NEVER zero animation-duration instead — duration 0 on an infinite
     animation is an infinite loop of zero-length iterations and pegged the iframe's main thread at ~4fps
     for the whole seeded stream (measured live). */
  quietStyle=document.createElement('style');
  quietStyle.textContent='[data-bds-seed],[data-bds-seed] *{animation-delay:-9999s !important}';
  document.head.appendChild(quietStyle);
}
function markSeeded(root){
  seededSet.add(root);
  root.setAttribute('data-bds-seed','');
  var els=root.querySelectorAll('*');
  for(var i=0;i<els.length;i++)seededSet.add(els[i]);
}

function hardReset(){
  stage.innerHTML='';
  inserted=[];flashed=new WeakSet();scriptsRan=false;seeded=false;
  for(var i=0;i<headStyles.length;i++){if(headStyles[i].parentNode)headStyles[i].parentNode.removeChild(headStyles[i])}
  headStyles=[];
  dropSeedStyles();
  if(quietStyle&&quietStyle.parentNode)quietStyle.parentNode.removeChild(quietStyle);
  quietStyle=null;
}

function dropSeedStyles(){
  for(var i=0;i<seedStyles.length;i++){if(seedStyles[i].parentNode)seedStyles[i].parentNode.removeChild(seedStyles[i])}
  seedStyles=[];
}

function render(){
  var target;
  try{target=parser.parseFromString(raw,'text/html')}catch(err){return}
  if(!target||!target.body)return;
  syncHead(target);
  /* The streamed head is complete once the body starts — the seed's stylesheet has been fully superseded
     by the new one sitting after it, so it can go without any un-styled window. */
  if(seedStyles.length&&target.body.firstChild)dropSeedStyles();
  sync(stage,target.body,true);
  /* Skeleton over → tell the host: the card swaps its Lottie waiting overlay for the streaming content
     on exactly this edge (the shell alone knows when the first paintable element lands). */
  if(skel.style.display!=='none'&&stage.firstElementChild){skel.style.display='none';try{parent.postMessage({__bds_skel_done:1},'*')}catch(e){}}
  sweep();
}

function syncHead(target){
  if(!target.head)return;
  var els=target.head.children,styleIdx=0,i;
  for(i=0;i<els.length;i++){
    var el=els[i];
    if(el.tagName==='STYLE'){
      var mine=headStyles[styleIdx];
      if(!mine){mine=document.createElement('style');document.head.appendChild(mine);headStyles[styleIdx]=mine}
      if(mine.textContent!==el.textContent)mine.textContent=el.textContent;
      styleIdx++;
    }else if(el.tagName==='LINK'){
      var href=el.getAttribute('href')||'';
      if(href&&!linksAdded[href]){
        linksAdded[href]=1;
        var l=document.createElement('link');
        l.rel=el.getAttribute('rel')||'stylesheet';l.href=href;
        document.head.appendChild(l);
      }
    }
  }
}

/* Attribute equality that IGNORES the shell's own data-bds-seed marker (a seeded live node carries it,
   the streamed target never does — without the exemption every seeded node would read as changed and be
   replaced on the first sync, blanking the very content seeding exists to keep). */
function sameAttrs(live,target){
  var marker=live.hasAttribute('data-bds-seed')?1:0;
  if(live.attributes.length-marker!==target.attributes.length)return false;
  for(var i=0;i<target.attributes.length;i++){
    var at=target.attributes[i];
    if(live.getAttribute(at.name)!==at.value)return false;
  }
  return true;
}

/* SIGNIFICANT nodes only: whitespace-only text and comments are invisible, and the model re-emits a
   document with its own indentation — letting those nodes into the index pairing shifted every position
   and cascaded "replacements" of visually identical content (the per-element flicker on edits, observed
   live). They are never cloned and never counted. */
function sig(parent){
  var out=[],cs=parent.childNodes,i,n;
  for(i=0;i<cs.length;i++){
    n=cs[i];
    if(n.nodeType===8)continue;
    if(n.nodeType===3&&!/\\S/.test(n.nodeValue))continue;
    out.push(n);
  }
  return out;
}

/* isSpineLevel: this level contains the write head (last target position may still be growing). */
function sync(live,target,isSpineLevel){
  /* lcs is a SNAPSHOT: replaceAt swaps in place (later references stay valid) and appendClone only fires
     once live pairs are exhausted, so pairing by node identity against the snapshot stays correct. */
  var tc=sig(target),lcs=sig(live),lc,i,t,l;
  for(i=0;i<tc.length;i++){
    t=tc[i];l=lcs[i];
    var isTail=isSpineLevel&&i===tc.length-1;
    if(!l){appendClone(live,t);continue}
    var seededNode=l.nodeType===1&&seededSet.has(l);
    if(l.nodeType!==t.nodeType||l.nodeName!==t.nodeName){
      replaceAt(live,l,t);continue;
    }
    if(t.nodeType===1){
      /* Seeded element whose replacement differs in attributes → the edit changed it: replace whole node.
         (Attrs are complete on first parse, so this check is exact.) The marker attribute STAYS on
         confirmed seeded nodes — removing it would re-arm their entrance animations. */
      if(seededNode&&!sameAttrs(l,t)){replaceAt(live,l,t);continue}
      sync(l,t,isTail);
    }else if(l.nodeValue!==t.nodeValue){
      l.nodeValue=t.nodeValue;
      /* A text rewrite on a settled (non-tail) node is an EDIT landing — flash its element. The growing
         tail is the live writing itself; flashing it every chunk would strobe. */
      if(!isTail&&l.parentNode&&l.parentNode.nodeType===1&&l.parentNode!==stage)maybeFlash(l.parentNode,true);
    }
  }
  /* Extra live children beyond the target: in seeded mode these are the OLD document's tail — keep them
     visible until the stream writes past them; trim only at done. A fresh stream trims immediately. */
  if(!seeded||done){
    lc=sig(live);
    while(lc.length>tc.length){live.removeChild(lc[lc.length-1]);lc.pop()}
  }
}

function replaceAt(live,l,t){
  var node=t.cloneNode(true);
  live.replaceChild(node,l);
  if(node.nodeType===1)recordEls(node);
}

function appendClone(live,t){
  /* Append only happens when the target index is past every live child — in seeded mode that means the
     old tail at this level was already consumed by positional replacement, so end-append is correct. */
  var node=t.cloneNode(true);
  live.appendChild(node);
  if(node.nodeType===1)recordEls(node);
}

function recordEls(root){
  inserted.push(root);
  var els=root.querySelectorAll('*');
  for(var i=0;i<els.length;i++)inserted.push(els[i]);
}

function sweep(){
  var tail=inserted[inserted.length-1];
  if(!tail)return;
  for(var i=0;i<inserted.length-1;i++){
    var el=inserted[i];
    if(flashed.has(el))continue;
    if(el.contains(tail))continue;
    flashed.add(el);
    maybeFlash(el,false);
  }
}

function finishFlashes(){
  for(var i=0;i<inserted.length;i++){
    var el=inserted[i];
    if(flashed.has(el))continue;
    flashed.add(el);
    maybeFlash(el,false);
  }
}

function maybeFlash(el,force){
  if(!force&&el.firstElementChild)return;
  var tag=el.tagName;
  if(tag==='SCRIPT'||tag==='STYLE'||tag==='LINK'||tag==='BR')return;
  if(!el.isConnected)return;
  var r=el.getBoundingClientRect();
  if(r.width<24||r.height<10)return;
  if(active.length>24)return;
  var d=document.createElement('div');
  d.className='flash';
  place(d,r);
  lay.appendChild(d);
  var rec={d:d,node:el};
  active.push(rec);
  setTimeout(function(){
    if(d.parentNode)d.parentNode.removeChild(d);
    var ix=active.indexOf(rec);
    if(ix>=0)active.splice(ix,1);
  },1200);
}

function place(d,r){d.style.left=r.left+'px';d.style.top=r.top+'px';d.style.width=r.width+'px';d.style.height=r.height+'px'}

(function anchor(){
  for(var i=0;i<active.length;i++){
    if(active[i].node.isConnected)place(active[i].d,active[i].node.getBoundingClientRect());
  }
  requestAnimationFrame(anchor);
})();

function runScripts(){
  if(scriptsRan)return;
  scriptsRan=true;
  var list=stage.querySelectorAll('script');
  for(var i=0;i<list.length;i++){
    var run=document.createElement('script');
    run.textContent=list[i].textContent;
    document.body.appendChild(run);
  }
}
})();
<\/script></body></html>`
}
